/**
 * UsersPage - Admin page for managing users.
 *
 * Displays a table of all users with CRUD operations.
 */

import { useUsers, useUserMutations, useUserDialog } from '../hooks';
import { formatUserDate, isCreateUserFormData } from '../utils';
import { UserFormDialog, UserTable, UsersPageHeader } from '../components/organisms';
import { UserTableError } from '../components/molecules';
import type { UserResponse } from '@/lib/api';
import type { CreateUserFormData, UpdateUserFormData } from '../validation/userFormSchemas';

export function UsersPage() {
  const { data: users, isLoading, error } = useUsers();
  const { createUser, updateUser, deleteUser, isMutating } = useUserMutations();
  const { dialogMode, editingUser, openCreateDialog, openEditDialog, closeDialog, isOpen } = useUserDialog();

  const handleDelete = async (user: UserResponse) => {
    if (!confirm(`Are you sure you want to delete user ${user.email}?`)) {
      return;
    }
    await deleteUser(user);
  };

  const handleEdit = async (user: UserResponse) => {
    openEditDialog(user);
  };

  const handleSave = async (data: CreateUserFormData | UpdateUserFormData) => {
    try {
      if (dialogMode === 'edit' && editingUser) {
        // In edit mode, data is always UpdateUserFormData
        await updateUser(editingUser.id, data as UpdateUserFormData);
      } else {
        // In create mode, data is always CreateUserFormData
        // Use type guard for additional safety
        if (isCreateUserFormData(data)) {
          await createUser(data);
        } else {
          throw new Error('Invalid form data for create operation');
        }
      }
      closeDialog();
    } catch {
      // Error already handled by useUserMutations
    }
  };

  if (error) {
    return <UserTableError message="Failed to load users. Please try again." />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <UsersPageHeader onAddUser={openCreateDialog} />

      <UserTable
        users={users || []}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        formatDate={formatUserDate}
      />

      {isOpen && <UserFormDialog user={editingUser} onClose={closeDialog} onSave={handleSave} isLoading={isMutating} />}
    </div>
  );
}
